<template>
  <div :class="fieldClasses">
    <!-- Label -->
    <label 
      v-if="label" 
      :for="fieldId"
      :class="labelClasses"
      :style="labelStyle"
    >
      <span class="themed-form-field__label-content">
        <span class="themed-form-field__label-text">
          {{ label }}
          <span v-if="required" class="required-indicator">*</span>
        </span>
        <info-icon v-if="infoIcon" :identifier="infoIcon" class="themed-form-field__info-icon" />
        <span 
          v-if="tooltip" 
          class="themed-form-field__tooltip"
          :class="tooltipAlignClass"
          :data-tooltip="tooltip"
        >
          <theme-icon icon="help-circle" type="icons" classes="w-3.5 h-3.5" />
        </span>
      </span>
      <slot name="label-suffix" />
    </label>
    
    <!-- Input wrapper with optional icon -->
    <div :class="inputWrapperClasses">
      <theme-icon 
        v-if="icon" 
        :icon="icon" 
        :type="iconType"
        :classes="'w-5 h-5 themed-form-field__icon'"
      />
      <slot 
        :fieldId="fieldId"
        :errorId="errorId"
        :helpId="helpId"
        :hasError="hasError"
        :required="required"
      />
    </div>
    
    <!-- Error message -->
    <div 
      v-if="errorMessage" 
      :id="errorId"
      class="themed-form-field__error"
      role="alert"
      aria-live="polite"
    >
      {{ errorMessage }}
    </div>
    
    <!-- Help text -->
    <div 
      v-if="helpText && !errorMessage" 
      :id="helpId"
      class="themed-form-field__help"
    >
      {{ helpText }}
    </div>
  </div>
</template>

<script>
let fieldIdCounter = 0;

export default {
  name: 'ThemedFormField',
  props: {
    label: {
      type: String,
      default: ''
    },
    error: {
      type: [String, Array],
      default: null
    },
    helpText: {
      type: String,
      default: ''
    },
    tooltip: {
      type: String,
      default: ''
    },
    tooltipAlign: {
      type: String,
      default: 'center',
      validator: (v) => ['left', 'center', 'right'].includes(v)
    },
    required: {
      type: Boolean,
      default: false
    },
    layout: {
      type: String,
      default: 'vertical',
      validator: (v) => ['vertical', 'horizontal', 'inline'].includes(v)
    },
    labelWidth: {
      type: String,
      default: '200px'
    },
    icon: {
      type: String,
      default: ''
    },
    iconType: {
      type: String,
      default: 'icons'
    },
    infoIcon: {
      type: String,
      default: ''
    },
    fieldName: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      uniqueId: `field-${++fieldIdCounter}`
    };
  },
  computed: {
    fieldId() {
      return this.fieldName ? `field-${this.fieldName}` : this.uniqueId;
    },
    errorId() {
      return `${this.fieldId}-error`;
    },
    helpId() {
      return `${this.fieldId}-help`;
    },
    fieldClasses() {
      return [
        'themed-form-field',
        `themed-form-field--${this.layout}`,
        { 'themed-form-field--error': this.hasError },
        { 'themed-form-field--with-icon': this.icon }
      ];
    },
    labelClasses() {
      return [
        'themed-form-field__label',
        { 'themed-form-field__label--required': this.required }
      ];
    },
    labelStyle() {
      if (this.layout === 'horizontal' && this.labelWidth) {
        return { width: this.labelWidth };
      }
      return null;
    },
    inputWrapperClasses() {
      return [
        'themed-form-field__input-wrapper',
        { 'themed-form-field__input-wrapper--with-icon': this.icon }
      ];
    },
    errorMessage() {
      if (!this.error) return null;
      return Array.isArray(this.error) ? this.error[0] : this.error;
    },
    hasError() {
      return Boolean(this.errorMessage);
    },
    tooltipAlignClass() {
      return `tooltip-align-${this.tooltipAlign}`;
    }
  }
}
</script>
